import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { PublicShell } from "@/pages/public/PublicQuote";
import { inr } from "@/lib/api";
import { LogOut, Package, FileText, Receipt, Image as ImageIcon, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stageBadge = { procurement:"badge-warn", material_received:"badge-info", qc1:"badge-info",
  customization:"badge-warn", qc2:"badge-info", packing:"badge-warn",
  dispatched:"badge-success", delivered:"badge-success" };

export default function ClientPortal() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("client_token");
    if (!token) { nav("/portal/login"); return; }
    axios.get(`${API}/client-portal/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setD(r.data))
      .catch(() => { setErr("Session expired — please sign in again."); localStorage.removeItem("client_token"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("client_token"); localStorage.removeItem("client_name"); nav("/portal/login"); };

  if (err) return <PublicShell><p className="text-center p-8 text-[#B83A3A]">{err}</p></PublicShell>;
  if (!d) return <PublicShell><p className="text-center p-8 text-[#757169]">Loading…</p></PublicShell>;

  return (
    <PublicShell>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="label-tiny mb-1">Client Portal</div>
          <h1 className="font-display text-3xl tracking-tight">Hi, {d.customer.name}</h1>
          <div className="text-sm text-[#757169] mt-1">{d.summary.orders} orders · {d.summary.invoices} invoices · outstanding {inr(d.summary.outstanding)}</div>
        </div>
        <button onClick={logout} className="btn-ghost text-xs flex items-center gap-1" data-testid="client-logout"><LogOut size={12}/> Sign out</button>
      </div>

      <Section title="Your orders" icon={Package} empty="No orders yet.">
        {d.orders.map(o => (
          <div key={o.id} className="card-surface p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="mono font-semibold">{o.order_no}</span>
              <span className={`badge ${stageBadge[o.stage]||"badge-neutral"}`}>{o.stage}</span>
            </div>
            <div className="text-sm text-[#757169]">{o.items?.length || 0} items · <span className="mono">{inr(o.total)}</span></div>
          </div>
        ))}
      </Section>

      <Section title="Your quotes" icon={FileText} empty="No quotes on file.">
        {d.quotes.map(q => (
          <a key={q.id} href={`/q/${q.token}`} target="_blank" rel="noreferrer" className="card-surface p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-1">
              <span className="mono font-semibold">{q.quote_no}</span>
              <span className={`badge ${q.status==="accepted"?"badge-success":q.status==="declined"?"badge-danger":"badge-neutral"}`}>{q.status}</span>
            </div>
            <div className="text-sm text-[#757169]"><span className="mono">{inr(q.total)}</span> — view →</div>
          </a>
        ))}
      </Section>

      <Section title="Your invoices" icon={Receipt} empty="No invoices yet.">
        {d.invoices.map(i => (
          <div key={i.id} className="card-surface p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="mono font-semibold">{i.invoice_no}</span>
              <span className={`badge ${i.status==="paid"?"badge-success":i.status==="partial"?"badge-warn":"badge-neutral"}`}>{i.status}</span>
            </div>
            <div className="text-sm text-[#757169]">Paid <span className="mono">{inr(i.amount_paid)}</span> / <span className="mono">{inr(i.total)}</span></div>
          </div>
        ))}
      </Section>

      <Section title="Your files" icon={ImageIcon} empty="No files uploaded.">
        {d.attachments.map(a => (
          <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer" className="card-surface overflow-hidden hover:shadow-md">
            {a.file_url?.startsWith("data:image") || /\.(png|jpe?g|gif|webp|svg)$/i.test(a.file_name)
              ? <img src={a.file_url} alt={a.file_name} className="w-full h-28 object-cover"/>
              : <div className="h-28 bg-[#F6F1E7] flex items-center justify-center text-[#757169] text-xs p-2 text-center">{a.file_name}</div>}
            <div className="p-2">
              <div className="text-xs font-medium truncate">{a.file_name}</div>
              <div className="text-[10px] text-[#757169] uppercase tracking-wider">{a.type}</div>
            </div>
          </a>
        ))}
      </Section>
    </PublicShell>
  );
}

const Section = ({ title, icon: Icon, empty, children }) => {
  const kids = React.Children.toArray(children);
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#757169]"/>
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      {kids.length === 0 ? <div className="text-sm text-[#757169]">{empty}</div>
        : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{kids}</div>}
    </div>
  );
};
